import { create } from 'zustand'
import { CreativeService } from '../../../openapi/services/CreativeService'
import {
  type CreativeProcessSnapshot,
  type RubricDimensions,
  type RubricGates,
  type ScoreHistoryPoint,
  mapHistory,
  mapSnapshot,
} from '../process/creative-process.types'

/** The live re-score payload pushed over `creative:score:updated`. */
export interface ScoreUpdatePayload {
  score: number
  verdict: string
  dimensions?: RubricDimensions
  gates?: RubricGates
  singleFix?: string
  scoreBefore?: number
  source?: string
}

interface CreativeProcessState {
  snapshot: CreativeProcessSnapshot | null
  history: ScoreHistoryPoint[]
  loading: boolean
  /** True while a re-score is in flight (Phase 2) — drives the panel's pulse. */
  rescoring: boolean
  /** The previous overall score, kept so the panel can animate a delta. */
  previousScore?: number
}

const EMPTY: CreativeProcessState = {
  snapshot: null,
  history: [],
  loading: false,
  rescoring: false,
}

interface CreativeProcessStore {
  byCreative: Record<string, CreativeProcessState>
  /** Fetch the latest snapshot + trail for a creative from the server. */
  fetchGeneration: (creativeId: string) => Promise<void>
  setRescoring: (creativeId: string, rescoring: boolean) => void
  /** Apply a live score update from Pusher, merging onto the existing snapshot. */
  applyScoreUpdate: (creativeId: string, update: ScoreUpdatePayload) => void
  get: (creativeId: string) => CreativeProcessState
}

export const useCreativeProcessStore = create<CreativeProcessStore>((set, get) => ({
  byCreative: {},

  get: creativeId => get().byCreative[creativeId] ?? EMPTY,

  fetchGeneration: async creativeId => {
    set(state => ({
      byCreative: {
        ...state.byCreative,
        [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), loading: true },
      },
    }))
    try {
      const dto = await CreativeService.creativeControllerGetGenerationV1(creativeId)
      const snapshot = mapSnapshot(dto?.snapshot)
      const history = mapHistory(dto)
      set(state => ({
        byCreative: {
          ...state.byCreative,
          [creativeId]: {
            ...(state.byCreative[creativeId] ?? EMPTY),
            snapshot,
            history,
            loading: false,
            rescoring: false,
          },
        },
      }))
    } catch (err) {
      console.error('Failed to fetch creative generation process:', err)
      set(state => ({
        byCreative: {
          ...state.byCreative,
          [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), loading: false },
        },
      }))
    }
  },

  setRescoring: (creativeId, rescoring) =>
    set(state => ({
      byCreative: {
        ...state.byCreative,
        [creativeId]: { ...(state.byCreative[creativeId] ?? EMPTY), rescoring },
      },
    })),

  applyScoreUpdate: (creativeId, update) =>
    set(state => {
      const prev = state.byCreative[creativeId] ?? EMPTY
      // Merge the new score onto the existing snapshot so brief/angle/design tier
      // and the revise trail survive a copy-only re-score.
      const merged: CreativeProcessSnapshot = {
        ...(prev.snapshot ?? { score: update.score, verdict: update.verdict }),
        score: update.score,
        verdict: update.verdict,
        dimensions: update.dimensions ?? prev.snapshot?.dimensions,
        gates: update.gates ?? prev.snapshot?.gates,
        singleFix: update.singleFix ?? prev.snapshot?.singleFix,
      }
      const point: ScoreHistoryPoint = {
        score: update.score,
        verdict: update.verdict,
        source: update.source,
        createdAt: new Date().toISOString(),
      }
      return {
        byCreative: {
          ...state.byCreative,
          [creativeId]: {
            ...prev,
            previousScore: update.scoreBefore ?? prev.snapshot?.score,
            snapshot: merged,
            history: [...prev.history, point],
            rescoring: false,
          },
        },
      }
    }),
}))
